import type { JobScriptFE } from "Projects/Components/ScriptEditor/ScriptEditorContext";
import {
  isMediaSegment,
  isPauseSegment,
  isVoiceSegment,
} from "Projects/Components/ScriptEditor/ScriptEditorContext";
import { extractVariableNames } from "./parseSegmentText";

export type ScriptValidationError =
  | { type: "emptyScript" }
  | { segmentIndex: number; type: "noActor" }
  | { segmentIndex: number; type: "emptyText" }
  | { segmentIndex: number; type: "invalidSpeedFactor" }
  | { segmentIndex: number; type: "noFile" }
  | { segmentIndex: number; type: "invalidStartTime" }
  | { segmentIndex: number; type: "zeroDuration" }
  | { segmentIndex: number; type: "missingVariable"; varName: string };

const SPEED_FACTOR_MIN = 0.7;
const SPEED_FACTOR_MAX = 1.2;

export function validateScript(script: JobScriptFE): ScriptValidationError[] {
  const errors: ScriptValidationError[] = [];

  if (script.segments.length === 0) {
    errors.push({ type: "emptyScript" });
    return errors;
  }

  const varNames = new Set(script.variables.map((v) => v.name));

  script.segments.forEach((seg, idx) => {
    if (isVoiceSegment(seg)) {
      if (!seg.actorVoiceID) {
        errors.push({ segmentIndex: idx, type: "noActor" });
      }

      const fullText = seg.spans.map((s) => s.text).join("");
      if (!fullText.trim()) {
        errors.push({ segmentIndex: idx, type: "emptyText" });
      }

      if (
        seg.speedFactor < SPEED_FACTOR_MIN ||
        seg.speedFactor > SPEED_FACTOR_MAX
      ) {
        errors.push({ segmentIndex: idx, type: "invalidSpeedFactor" });
      }

      for (const name of extractVariableNames(fullText)) {
        if (!varNames.has(name)) {
          errors.push({
            segmentIndex: idx,
            type: "missingVariable",
            varName: name,
          });
        }
      }
    } else if (isPauseSegment(seg)) {
      if (seg.durationMs <= 0) {
        errors.push({ segmentIndex: idx, type: "zeroDuration" });
      }
    } else if (isMediaSegment(seg)) {
      if (!seg.partnerMediaFileID) {
        errors.push({ segmentIndex: idx, type: "noFile" });
      }
      if (seg.startAtMs < 0) {
        errors.push({ segmentIndex: idx, type: "invalidStartTime" });
      }
    }
  });

  return errors;
}

export function hasValidationErrors(errors: ScriptValidationError[]): boolean {
  return errors.length > 0;
}

export function getSegmentErrors(
  errors: ScriptValidationError[],
  segmentIndex: number,
): ScriptValidationError[] {
  return errors.filter(
    (e) => "segmentIndex" in e && e.segmentIndex === segmentIndex,
  );
}
